import {
  useCallback,
  useDeferredValue,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import type { FunctionArgs, FunctionReturnType } from 'convex/server';
import { adminSession } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { renderPost, slugify } from '../../../convex/functions/lib/blog/markdown';
import {
  BLOG_LIMITS,
  CTA_OPTIONS,
  DEFAULT_AUTHOR,
  IMAGE_TYPES,
  altFromFilename,
  blogError,
  formatDateTime,
  publicPostUrl,
  siteHost,
  toLocalInput,
  uploadBlogImage,
  type BlogField,
  type CtaPath,
  type PostStatus,
} from '@/lib/blog';
import {
  Counter,
  ErrorBanner,
  FieldError,
  FieldLabel,
  Segmented,
  StatusBadge,
  dangerButton,
  disabledStyle,
  inputStyle,
  primaryButton,
  secondaryButton,
} from '@/components/BlogUi';
import '@/styles/blogPreview.css';

type AdminPost = NonNullable<FunctionReturnType<typeof api.admin.blog.getPost>>;
type PostFields = FunctionArgs<typeof api.admin.blog.savePost>['fields'];

const AUTOSAVE_MS = 30_000;

/**
 * /dashboard/blog/:id, where :id is a post id or "new". One route for both,
 * so the editor keeps its state (cursor, undo, open panels) when a new post
 * is first saved and the URL switches from /new to its id.
 */
export function BlogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = adminSession.getToken() ?? '';
  const isNew = !id || id === 'new';
  // The id this editor instance created. Navigating to it must not remount.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    if (isNew && createdId) {
      setCreatedId(null);
      setEpoch((n) => n + 1);
    }
  }, [isNew, createdId]);

  const ownsId = !isNew && id === createdId;
  const post = useQuery(
    api.admin.blog.getPost,
    token && !isNew && !ownsId ? { token, postId: id as Id<'blogPosts'> } : 'skip',
  );

  const onCreated = useCallback(
    (postId: string) => {
      setCreatedId(postId);
      navigate(`/dashboard/blog/${postId}`, { replace: true });
    },
    [navigate],
  );

  if (!isNew && !ownsId) {
    if (post === undefined) return <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>;
    if (post === null) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ color: 'var(--ink-3)' }}>Post not found.</div>
          <Link to="/dashboard/blog">Back to Blog</Link>
        </div>
      );
    }
  }

  const key = isNew || ownsId ? `new-${epoch}` : (id as string);
  return <Editor key={key} token={token} initial={isNew || ownsId ? null : (post ?? null)} onCreated={onCreated} />;
}

// ── Form model ────────────────────────────────────────────────────────

interface Form {
  title: string;
  slug: string;
  /** False while the slug still follows the title. */
  slugTouched: boolean;
  body: string;
  excerpt: string;
  pillarSlug: string;
  tags: string[];
  coverKey: string | null;
  coverUrl: string | null;
  coverAlt: string;
  seoTitle: string;
  canonicalUrl: string;
  authorName: string;
  ctaPath: CtaPath | '';
  ctaLabel: string;
}

function formFromPost(p: AdminPost | null): Form {
  if (!p) {
    return {
      title: '',
      slug: '',
      slugTouched: false,
      body: '',
      excerpt: '',
      pillarSlug: '',
      tags: [],
      coverKey: null,
      coverUrl: null,
      coverAlt: '',
      seoTitle: '',
      canonicalUrl: '',
      authorName: DEFAULT_AUTHOR,
      ctaPath: '',
      ctaLabel: '',
    };
  }
  // A slug keeps following the title only on a draft that never went live
  // and whose slug still matches; anything else would silently move a URL.
  const follows = p.status === 'draft' && p.firstPublishedAt === null && p.slug === slugify(p.title);
  return {
    title: p.title,
    slug: p.slug,
    slugTouched: !follows,
    body: p.bodyMarkdown,
    excerpt: p.excerpt,
    pillarSlug: p.pillar?.slug ?? '',
    tags: p.tags.map((t) => t.name),
    coverKey: p.cover?.key ?? null,
    coverUrl: p.cover?.url ?? null,
    coverAlt: p.cover?.alt ?? '',
    seoTitle: p.seoTitle ?? '',
    canonicalUrl: p.canonicalUrl ?? '',
    authorName: p.authorName,
    ctaPath: p.productCta?.path ?? '',
    ctaLabel: p.productCta?.label ?? '',
  };
}

function effectiveSlug(f: Form): string {
  return f.slugTouched ? f.slug : slugify(f.title);
}

/** What counts for "unsaved changes": everything the server stores. */
function snapshot(f: Form): string {
  const { slugTouched: _ignored, coverUrl: _url, ...rest } = f;
  return JSON.stringify({ ...rest, slug: effectiveSlug(f) });
}

function toFields(f: Form, isNew: boolean): PostFields {
  const fields: PostFields = {
    title: f.title.trim(),
    excerpt: f.excerpt.trim(),
    bodyMarkdown: f.body,
    tags: f.tags,
    authorName: f.authorName.trim() || DEFAULT_AUTHOR,
    seoTitle: f.seoTitle.trim() || null,
    canonicalUrl: f.canonicalUrl.trim() || null,
    productCta: f.ctaPath ? { path: f.ctaPath, ...(f.ctaLabel.trim() ? { label: f.ctaLabel.trim() } : {}) } : null,
    coverKey: f.coverKey,
  };
  if (f.coverKey) fields.coverAlt = f.coverAlt.trim();
  if (f.pillarSlug) fields.pillarSlug = f.pillarSlug;
  // A new post with an untouched slug lets the backend pick a free one.
  const slug = effectiveSlug(f);
  if (slug && (f.slugTouched || !isNew)) fields.slug = slug;
  return fields;
}

type Errors = Partial<Record<BlogField, string>>;

function validateForLive(f: Form): Errors {
  const errs: Errors = {};
  if (!f.title.trim()) errs.title = 'Add a title.';
  if (!f.pillarSlug) errs.pillar = 'Choose a pillar.';
  const n = f.excerpt.trim().length;
  if (n < BLOG_LIMITS.excerptMin || n > BLOG_LIMITS.excerptMax) {
    errs.excerpt = `Publishing needs a ${BLOG_LIMITS.excerptMin}–${BLOG_LIMITS.excerptMax} character excerpt (it has ${n}).`;
  }
  if (!f.body.trim()) errs.body = 'Write the post before publishing it.';
  return errs;
}

// ── Layout ────────────────────────────────────────────────────────────

type Layout = 'split' | 'stacked';

function useLayout(ref: React.RefObject<HTMLElement>): Layout {
  const [layout, setLayout] = useState<Layout>('split');
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const pick = (w: number) => setLayout(w >= 700 ? 'split' : 'stacked');
    pick(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => pick(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return layout;
}

// ── Editor ────────────────────────────────────────────────────────────

function Editor({
  token,
  initial,
  onCreated,
}: {
  token: string;
  initial: AdminPost | null;
  onCreated: (postId: string) => void;
}) {
  const [postId, setPostId] = useState<Id<'blogPosts'> | null>(initial?._id ?? null);
  const live = useQuery(api.admin.blog.getPost, token && postId ? { token, postId } : 'skip');
  const pillars = useQuery(api.admin.blog.listPillars, token ? { token } : 'skip');
  const allTags = useQuery(api.admin.blog.listTags, token ? { token } : 'skip');
  const savePost = useMutation(api.admin.blog.savePost);
  const presign = useMutation(api.admin.blog.presignImage);

  const [form, setForm] = useState<Form>(() => formFromPost(initial));
  const [baseline, setBaseline] = useState<string>(() => snapshot(formFromPost(initial)));
  const [savedStatus, setSavedStatus] = useState<PostStatus>(initial?.status ?? 'draft');
  const [busy, setBusy] = useState<null | 'save' | 'publish' | 'schedule' | 'unpublish' | 'archive' | 'autosave'>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [autosaveFailed, setAutosaveFailed] = useState(false);
  const [uploads, setUploads] = useState(0);
  const [pane, setPane] = useState<'write' | 'preview'>('write');
  const [scheduleAt, setScheduleAt] = useState(() =>
    initial?.publishAt ? toLocalInput(initial.publishAt) : toLocalInput(Date.now() + 24 * 3600_000),
  );
  const [confirmArchive, setConfirmArchive] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const layout = useLayout(rootRef);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const status: PostStatus = live?.status ?? savedStatus;
  const liveSlug = live?.slug ?? initial?.slug ?? null;
  const dirty = snapshot(form) !== baseline;
  const slug = effectiveSlug(form);

  const set = useCallback(<K extends keyof Form>(key: K, value: Form[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    const field = FORM_TO_FIELD[key];
    if (field) setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));
  }, []);

  // ── Saving ──

  const save = useCallback(
    async (
      mode: 'save' | 'publish' | 'schedule' | 'unpublish' | 'archive' | 'autosave',
      publishAt?: number,
    ): Promise<boolean> => {
      const sent = form;
      const isNew = postId === null;
      const target: PostStatus | undefined =
        mode === 'publish' ? 'published' : mode === 'schedule' ? 'scheduled' : mode === 'unpublish' ? 'draft' : mode === 'archive' ? 'archived' : undefined;

      if (target === 'published' || target === 'scheduled') {
        const errs = validateForLive(sent);
        if (target === 'scheduled' && (publishAt === undefined || !Number.isFinite(publishAt))) errs.schedule = 'Pick a date and time.';
        else if (target === 'scheduled' && publishAt! <= Date.now()) errs.schedule = 'Pick a time in the future, or publish now.';
        if (Object.keys(errs).length) {
          setErrors(errs);
          return false;
        }
      } else if (isNew) {
        const errs: Errors = {};
        if (!sent.title.trim()) errs.title = 'Add a title.';
        if (!sent.pillarSlug) errs.pillar = 'Choose a pillar.';
        if (Object.keys(errs).length) {
          setErrors(errs);
          return false;
        }
      }

      setBusy(mode);
      if (mode !== 'autosave') setErrors({});
      try {
        const fields: PostFields = {
          ...toFields(sent, isNew),
          ...(target ? { status: target } : isNew ? { status: 'draft' as const } : {}),
          ...(target === 'scheduled' ? { publishAt } : {}),
        };
        const res = await savePost({ token, ...(postId ? { postId } : {}), fields });
        // The backend may pick a different slug for a new post (e.g. "-2").
        const pin = (f: Form): Form =>
          !f.slugTouched && res.slug !== effectiveSlug(f) ? { ...f, slug: res.slug, slugTouched: true } : f;
        setForm((f) => pin(f));
        setBaseline(snapshot(pin(sent)));
        setSavedStatus(res.status);
        setSavedAt(Date.now());
        setAutosaveFailed(false);
        setConfirmArchive(false);
        if (isNew) {
          setPostId(res.postId);
          onCreated(res.postId);
        }
        return true;
      } catch (e) {
        const be = blogError(e, 'Blog editor');
        setErrors({ [be.field]: be.message });
        if (mode === 'autosave') setAutosaveFailed(true);
        return false;
      } finally {
        setBusy(null);
      }
    },
    [form, postId, savePost, token, onCreated],
  );

  // Autosave drafts every 30s: only a draft, only with a title and pillar.
  const autosaveRef = useRef<() => void>(() => undefined);
  autosaveRef.current = () => {
    if (!dirty || busy || uploads > 0 || status !== 'draft') return;
    if (!form.title.trim() || !form.pillarSlug) return;
    void save('autosave');
  };
  useEffect(() => {
    const t = setInterval(() => autosaveRef.current(), AUTOSAVE_MS);
    return () => clearInterval(t);
  }, []);

  useUnsavedChangesGuard(dirty || uploads > 0);

  // The form seeds from the server once; `live` keeps following it. Refresh
  // pulls in edits made elsewhere (another tab, the admin API) in place.
  const refresh = () => {
    if (!live) return;
    if (dirty && !window.confirm(REFRESH_PROMPT)) return;
    const next = formFromPost(live);
    setForm(next);
    setBaseline(snapshot(next));
    setSavedStatus(live.status);
    if (live.publishAt) setScheduleAt(toLocalInput(live.publishAt));
    setErrors({});
    setAutosaveFailed(false);
    setConfirmArchive(false);
  };

  // ── Images ──

  const insertAtCursor = useCallback((text: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      setForm((f) => ({ ...f, body: f.body + text }));
      return;
    }
    replaceRange(ta, ta.selectionStart, ta.selectionEnd, text, (v) => setForm((f) => ({ ...f, body: v })));
  }, []);

  const uploadIntoBody = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const marker = `![Uploading ${file.name.replace(/[[\]]/g, '')}…]()`;
        insertAtCursor(`${marker}\n`);
        setUploads((n) => n + 1);
        try {
          const { url } = await uploadBlogImage(presign, token, file);
          const alt = altFromFilename(file.name);
          setForm((f) => ({ ...f, body: f.body.replace(marker, `![${alt}](${url})`) }));
        } catch (e) {
          setForm((f) => ({ ...f, body: f.body.replace(`${marker}\n`, '').replace(marker, '') }));
          setErrors((er) => ({ ...er, body: blogError(e, 'Blog image upload').message }));
        } finally {
          setUploads((n) => n - 1);
        }
      }
    },
    [insertAtCursor, presign, token],
  );

  const imagesFrom = (list: FileList | null | undefined): File[] =>
    list ? Array.from(list).filter((f) => f.type.startsWith('image/')) : [];

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = imagesFrom(e.clipboardData?.files);
    if (files.length) {
      e.preventDefault();
      void uploadIntoBody(files);
    }
  };
  const onDrop = (e: DragEvent<HTMLTextAreaElement>) => {
    const files = imagesFrom(e.dataTransfer?.files);
    if (files.length) {
      e.preventDefault();
      textareaRef.current?.focus();
      void uploadIntoBody(files);
    }
  };

  // ── Preview ──

  const deferredBody = useDeferredValue(form.body);
  const rendered = useMemo(() => renderPost(deferredBody), [deferredBody]);

  // ── Render ──

  const isNew = postId === null;
  const primaryLabel =
    status === 'published' ? 'Save changes' : status === 'scheduled' ? 'Publish now' : 'Publish';
  const secondaryLabel = status === 'draft' ? 'Save draft' : 'Save changes';
  const saveState =
    busy === 'autosave' || busy === 'save'
      ? 'Saving…'
      : autosaveFailed && dirty
        ? 'Autosave failed'
        : dirty
          ? 'Unsaved changes'
          : savedAt
            ? `Saved ${new Date(savedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
            : '';

  const formError = errors.form;

  const header = (
    <div
      style={{
        position: 'sticky',
        // <main> scrolls and is padded (--main-pad); sticky offsets are
        // measured inside that padding, so top: 0 would leave a strip above
        // the header where the page scrolls through. The negative offset
        // pins it to the edge.
        top: `calc(-1 * ${MAIN_PAD})`,
        zIndex: 5,
        margin: `calc(-1 * ${MAIN_PAD}) calc(-1 * ${MAIN_PAD}) 0`,
        padding: `16px ${MAIN_PAD} 12px`,
        background: 'var(--bg)',
        borderBottom: '1px solid var(--line)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <Link to="/dashboard/blog" style={{ fontSize: 13, color: 'var(--ink-3)', fontWeight: 600 }}>
        ← Blog
      </Link>
      <StatusBadge status={status} />
      <span style={{ fontSize: 12, color: autosaveFailed && dirty ? '#a8331f' : 'var(--ink-3)' }} aria-live="polite">
        {uploads > 0 ? 'Uploading image…' : saveState}
      </span>
      <div style={{ flex: 1 }} />
      {status === 'published' && liveSlug && (
        <a href={publicPostUrl(liveSlug)} target="_blank" rel="noopener" style={{ ...secondaryButton, textDecoration: 'none' }}>
          View live
        </a>
      )}
      {!isNew && (
        <button
          type="button"
          title="Load the latest saved version of this post"
          style={{ ...secondaryButton, ...disabledStyle(busy !== null || uploads > 0 || !live) }}
          disabled={busy !== null || uploads > 0 || !live}
          onClick={refresh}
        >
          Refresh
        </button>
      )}
      {status !== 'published' && (
        <button
          type="button"
          style={{ ...secondaryButton, ...disabledStyle(busy !== null) }}
          disabled={busy !== null}
          onClick={() => void save('save')}
        >
          {busy === 'save' ? 'Saving…' : secondaryLabel}
        </button>
      )}
      <button
        type="button"
        style={{ ...primaryButton, ...disabledStyle(busy !== null || uploads > 0) }}
        disabled={busy !== null || uploads > 0}
        onClick={() => void save(status === 'published' ? 'save' : 'publish')}
      >
        {busy === 'publish' ? 'Publishing…' : busy === 'save' && status === 'published' ? 'Saving…' : primaryLabel}
      </button>
    </div>
  );

  const writePane = (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Toolbar
        onAction={(a) => {
          const ta = textareaRef.current;
          if (!ta) return;
          if (a === 'image') {
            pickFiles((files) => void uploadIntoBody(files));
            return;
          }
          applyFormat(ta, a, (v) => setForm((f) => ({ ...f, body: v })));
        }}
      />
      <textarea
        ref={textareaRef}
        aria-label="Post body (markdown)"
        value={form.body}
        onChange={(e) => set('body', e.target.value)}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => {
          if (Array.from(e.dataTransfer.items).some((i) => i.kind === 'file')) e.preventDefault();
        }}
        onKeyDown={(e) => onShortcut(e, (v) => setForm((f) => ({ ...f, body: v })))}
        placeholder="Write in markdown. Paste or drop images to upload them."
        spellCheck
        style={{
          ...inputStyle,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          fontFamily: 'var(--font-mono)',
          fontSize: 14,
          lineHeight: 1.6,
          minHeight: '62vh',
          resize: 'vertical',
          padding: '12px 14px',
          borderColor: errors.body ? '#a8331f' : 'var(--line-strong)',
        }}
      />
      <FieldError message={errors.body} />
    </div>
  );

  const previewPane = (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 12,
        padding: layout === 'split' ? '28px 28px 36px' : '20px 16px 28px',
        minWidth: 0,
        minHeight: '62vh',
      }}
    >
      {rendered.demotedH1 && (
        <div
          style={{ fontSize: 12, color: 'var(--ink-2)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 10px', marginBottom: 16 }}
        >
          <code className="mono"># </code> headings show as H2. The title is the page's only H1.
        </div>
      )}
      <article className="blog-preview">
        <h1 className="blog-preview-title">{form.title || 'Untitled post'}</h1>
        <div style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 20 }}>
          By {form.authorName.trim() || DEFAULT_AUTHOR} · {rendered.readingMinutes} min read
        </div>
        {form.coverUrl && (
          <figure>
            <img src={form.coverUrl} alt={form.coverAlt} />
          </figure>
        )}
        <div dangerouslySetInnerHTML={{ __html: rendered.html }} />
      </article>
    </div>
  );

  const toggle = (
    <div style={{ marginBottom: 10 }}>
      <Segmented<'write' | 'preview'>
        label="Editor view"
        options={[
          { value: 'write', label: 'Write' },
          { value: 'preview', label: 'Preview' },
        ]}
        value={pane}
        onChange={setPane}
      />
    </div>
  );

  const sidebar = (
    <Sidebar
      form={form}
      set={set}
      errors={errors}
      slug={slug}
      isNew={isNew}
      status={status}
      live={live ?? initial}
      pillars={pillars}
      allTags={allTags}
      busy={busy}
      token={token}
      presign={presign}
      setErrors={setErrors}
      setSlugFollowsTitle={() => setForm((f) => ({ ...f, slugTouched: false }))}
      setSlug={(v) => setForm((f) => ({ ...f, slug: v, slugTouched: true }))}
      setCover={(key, url) => setForm((f) => ({ ...f, coverKey: key, coverUrl: url }))}
      scheduleAt={scheduleAt}
      setScheduleAt={setScheduleAt}
      onSchedule={() => void save('schedule', scheduleAt ? new Date(scheduleAt).getTime() : undefined)}
      onUnpublish={() => void save('unpublish')}
      confirmArchive={confirmArchive}
      setConfirmArchive={setConfirmArchive}
      onArchive={() => void save('archive')}
    />
  );

  const gridColumns = layout === 'split' ? 'minmax(0, 1fr) 300px' : 'minmax(0, 1fr)';

  return (
    <div ref={rootRef}>
      {header}
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {formError && <ErrorBanner>{formError}</ErrorBanner>}
        <div>
          <input
            aria-label="Title"
            value={form.title}
            maxLength={BLOG_LIMITS.titleMax}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Post title"
            className="serif keep-size"
            style={{
              width: '100%',
              border: 'none',
              borderBottom: `1px solid ${errors.title ? '#a8331f' : 'var(--line-strong)'}`,
              background: 'transparent',
              color: 'var(--ink)',
              fontFamily: 'var(--font-display)',
              fontSize: 32,
              fontWeight: 500,
              padding: '4px 0 8px',
              outline: 'none',
            }}
          />
          <FieldError message={errors.title} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: gridColumns, gap: 16, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            {toggle}
            {pane === 'write' ? writePane : previewPane}
          </div>
          {sidebar}
        </div>
      </div>
    </div>
  );
}

// <main>'s padding, published by layout.css (40px on desktop, less on phones).
const MAIN_PAD = 'var(--main-pad, 40px)';

const FORM_TO_FIELD: Partial<Record<keyof Form, BlogField>> = {
  title: 'title',
  slug: 'slug',
  body: 'body',
  excerpt: 'excerpt',
  pillarSlug: 'pillar',
  tags: 'tags',
  coverKey: 'cover',
  coverAlt: 'cover',
  seoTitle: 'seoTitle',
  canonicalUrl: 'canonicalUrl',
  authorName: 'author',
  ctaPath: 'cta',
  ctaLabel: 'cta',
};

// ── Sidebar ───────────────────────────────────────────────────────────

type Pillars = FunctionReturnType<typeof api.admin.blog.listPillars> | undefined;
type Tags = FunctionReturnType<typeof api.admin.blog.listTags> | undefined;

function Sidebar(props: {
  form: Form;
  set: <K extends keyof Form>(key: K, value: Form[K]) => void;
  errors: Errors;
  slug: string;
  isNew: boolean;
  status: PostStatus;
  live: AdminPost | null | undefined;
  pillars: Pillars;
  allTags: Tags;
  busy: string | null;
  token: string;
  presign: Parameters<typeof uploadBlogImage>[0];
  setErrors: React.Dispatch<React.SetStateAction<Errors>>;
  setSlugFollowsTitle: () => void;
  setSlug: (v: string) => void;
  setCover: (key: string | null, url: string | null) => void;
  scheduleAt: string;
  setScheduleAt: (v: string) => void;
  onSchedule: () => void;
  onUnpublish: () => void;
  confirmArchive: boolean;
  setConfirmArchive: (v: boolean) => void;
  onArchive: () => void;
}) {
  const { form, set, errors, status, live, busy } = props;
  const [coverBusy, setCoverBusy] = useState(false);
  const excerptLen = form.excerpt.trim().length;
  const redirectsFrom = live?.redirectsFrom ?? [];
  const sorted = props.pillars ? [...props.pillars].sort((a, b) => a.order - b.order) : undefined;
  const slugChanged = live && props.slug !== live.slug && live.firstPublishedAt !== null;

  const uploadCover = () =>
    pickFiles(async ([file]) => {
      if (!file) return;
      setCoverBusy(true);
      props.setErrors((e) => ({ ...e, cover: undefined }));
      try {
        const { key, url } = await uploadBlogImage(props.presign, props.token, file);
        props.setCover(key, url);
        if (!form.coverAlt.trim()) set('coverAlt', altFromFilename(file.name));
      } catch (e) {
        props.setErrors((er) => ({ ...er, cover: blogError(e, 'Blog cover upload').message }));
      } finally {
        setCoverBusy(false);
      }
    }, false);

  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      <Section title="Publishing">
        <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
          {status === 'published' && live?.publishedAt
            ? `Live since ${formatDateTime(live.publishedAt)}`
            : status === 'scheduled' && live?.publishAt
              ? `Goes live ${formatDateTime(live.publishAt)}`
              : status === 'archived'
                ? 'Archived. Readers get a 410.'
                : 'Not published'}
        </div>
        {status !== 'archived' && status !== 'published' && (
          <div>
            <FieldLabel htmlFor="blog-schedule">{status === 'scheduled' ? 'Reschedule' : 'Schedule'}</FieldLabel>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                id="blog-schedule"
                type="datetime-local"
                value={props.scheduleAt}
                min={toLocalInput(Date.now())}
                onChange={(e) => props.setScheduleAt(e.target.value)}
                style={{ ...inputStyle, fontSize: 13, minWidth: 0, borderColor: errors.schedule ? '#a8331f' : 'var(--line-strong)' }}
              />
              <button
                type="button"
                style={{ ...secondaryButton, ...disabledStyle(busy !== null) }}
                disabled={busy !== null}
                onClick={props.onSchedule}
              >
                {busy === 'schedule' ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
            <FieldError message={errors.schedule} />
          </div>
        )}
        {!props.isNew && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(status === 'published' || status === 'scheduled' || status === 'archived') && (
              <button
                type="button"
                style={{ ...secondaryButton, ...disabledStyle(busy !== null) }}
                disabled={busy !== null}
                onClick={props.onUnpublish}
              >
                {busy === 'unpublish' ? 'Moving…' : status === 'archived' ? 'Restore to drafts' : 'Unpublish'}
              </button>
            )}
            {status !== 'archived' &&
              (props.confirmArchive ? (
                <>
                  <button type="button" style={secondaryButton} onClick={() => props.setConfirmArchive(false)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    style={{ ...dangerButton, ...disabledStyle(busy !== null) }}
                    disabled={busy !== null}
                    onClick={props.onArchive}
                  >
                    {busy === 'archive' ? 'Archiving…' : 'Archive post'}
                  </button>
                </>
              ) : (
                <button type="button" style={dangerButton} onClick={() => props.setConfirmArchive(true)}>
                  Archive
                </button>
              ))}
          </div>
        )}
      </Section>

      <Section title="URL">
        <div>
          <FieldLabel
            htmlFor="blog-slug"
            aside={
              form.slugTouched && (props.isNew || (status === 'draft' && live?.firstPublishedAt === null)) ? (
                <button type="button" onClick={props.setSlugFollowsTitle} style={linkButton}>
                  Match title
                </button>
              ) : null
            }
          >
            Slug
          </FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${errors.slug ? '#a8331f' : 'var(--line-strong)'}`, borderRadius: 8, background: 'var(--surface)' }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--ink-4)', paddingLeft: 10 }}>
              /blog/
            </span>
            <input
              id="blog-slug"
              value={props.slug}
              onChange={(e) => props.setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              style={{ ...inputStyle, border: 'none', paddingLeft: 2, fontFamily: 'var(--font-mono)', fontSize: 12, background: 'transparent' }}
            />
          </div>
          <FieldError message={errors.slug} />
          {slugChanged && !errors.slug && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>/blog/{live.slug} will redirect here.</div>
          )}
        </div>
        {redirectsFrom.length > 0 && (
          <div>
            <FieldLabel>Redirects from</FieldLabel>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {redirectsFrom.map((s) => (
                <li key={s} className="mono" style={{ fontSize: 12, color: 'var(--ink-3)', overflowWrap: 'anywhere' }}>
                  /blog/{s}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section title="Topic">
        <div>
          <FieldLabel htmlFor="blog-pillar">Pillar</FieldLabel>
          {sorted && sorted.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
              No pillars yet. <Link to="/dashboard/blog?tab=pillars">Create one</Link>
            </div>
          ) : (
            <select
              id="blog-pillar"
              value={form.pillarSlug}
              onChange={(e) => set('pillarSlug', e.target.value)}
              style={{ ...inputStyle, borderColor: errors.pillar ? '#a8331f' : 'var(--line-strong)' }}
            >
              <option value="">Choose a pillar</option>
              {sorted?.map((p) => (
                <option key={p._id} value={p.slug}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <FieldError message={errors.pillar} />
        </div>
        <div>
          <FieldLabel htmlFor="blog-tags" aside={<Counter n={form.tags.length} max={BLOG_LIMITS.tagsMax} />}>
            Tags
          </FieldLabel>
          <TagInput
            id="blog-tags"
            value={form.tags}
            onChange={(t) => set('tags', t)}
            suggestions={props.allTags?.map((t) => t.name) ?? []}
            invalid={Boolean(errors.tags)}
          />
          <FieldError message={errors.tags} />
        </div>
      </Section>

      <Section title="Summary">
        <div>
          <FieldLabel
            htmlFor="blog-excerpt"
            aside={
              <span
                className="mono"
                style={{
                  fontSize: 11,
                  color:
                    excerptLen > BLOG_LIMITS.excerptMax || (excerptLen > 0 && excerptLen < BLOG_LIMITS.excerptMin)
                      ? '#a8331f'
                      : excerptLen >= BLOG_LIMITS.excerptIdealMin
                        ? '#2E7D4F'
                        : 'var(--ink-4)',
                }}
                title={`Aim for ${BLOG_LIMITS.excerptIdealMin}–${BLOG_LIMITS.excerptMax}`}
              >
                {excerptLen}/{BLOG_LIMITS.excerptMax}
              </span>
            }
          >
            Excerpt
          </FieldLabel>
          <textarea
            id="blog-excerpt"
            value={form.excerpt}
            rows={4}
            onChange={(e) => set('excerpt', e.target.value.replace(/\n/g, ' '))}
            style={{ ...inputStyle, resize: 'vertical', fontSize: 13, borderColor: errors.excerpt ? '#a8331f' : 'var(--line-strong)' }}
          />
          <FieldError
            message={
              errors.excerpt ??
              (excerptLen > 0 && excerptLen < BLOG_LIMITS.excerptMin
                ? `Needs ${BLOG_LIMITS.excerptMin - excerptLen} more characters to publish.`
                : null)
            }
          />
        </div>
        <div>
          <FieldLabel>Search result</FieldLabel>
          <SearchPreview title={form.seoTitle.trim() || form.title.trim() || 'Untitled post'} slug={props.slug} excerpt={form.excerpt.trim()} />
        </div>
      </Section>

      <Section title="Cover image">
        {form.coverUrl ? (
          <img
            src={form.coverUrl}
            alt={form.coverAlt}
            style={{ width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: 8, background: 'var(--surface-2)' }}
          />
        ) : null}
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={{ ...secondaryButton, ...disabledStyle(coverBusy) }} disabled={coverBusy} onClick={uploadCover}>
            {coverBusy ? 'Uploading…' : form.coverUrl ? 'Replace' : 'Upload cover'}
          </button>
          {form.coverUrl && (
            <button type="button" style={dangerButton} onClick={() => props.setCover(null, null)}>
              Remove
            </button>
          )}
        </div>
        {form.coverKey && (
          <div>
            <FieldLabel htmlFor="blog-cover-alt" aside={<Counter n={form.coverAlt.length} max={BLOG_LIMITS.coverAltMax} />}>
              Alt text
            </FieldLabel>
            <input id="blog-cover-alt" value={form.coverAlt} onChange={(e) => set('coverAlt', e.target.value)} style={inputStyle} />
          </div>
        )}
        <FieldError message={errors.cover} />
      </Section>

      <Section title="SEO">
        <div>
          <FieldLabel htmlFor="blog-seo-title" aside={<Counter n={form.seoTitle.length} max={BLOG_LIMITS.seoTitleMax} />}>
            SEO title
          </FieldLabel>
          <input
            id="blog-seo-title"
            value={form.seoTitle}
            placeholder={form.title}
            onChange={(e) => set('seoTitle', e.target.value)}
            style={{ ...inputStyle, borderColor: errors.seoTitle ? '#a8331f' : 'var(--line-strong)' }}
          />
          <FieldError message={errors.seoTitle} />
        </div>
        <div>
          <FieldLabel htmlFor="blog-canonical">Canonical URL</FieldLabel>
          <input
            id="blog-canonical"
            type="url"
            value={form.canonicalUrl}
            placeholder="https://"
            onChange={(e) => set('canonicalUrl', e.target.value)}
            style={{ ...inputStyle, fontSize: 13, borderColor: errors.canonicalUrl ? '#a8331f' : 'var(--line-strong)' }}
          />
          <FieldError message={errors.canonicalUrl} />
        </div>
        <div>
          <FieldLabel htmlFor="blog-author">Author</FieldLabel>
          <input
            id="blog-author"
            value={form.authorName}
            maxLength={BLOG_LIMITS.authorMax}
            placeholder={DEFAULT_AUTHOR}
            onChange={(e) => set('authorName', e.target.value)}
            style={inputStyle}
          />
          <FieldError message={errors.author} />
        </div>
      </Section>

      <Section title="Call to action">
        <div>
          <FieldLabel htmlFor="blog-cta">Links to</FieldLabel>
          <select id="blog-cta" value={form.ctaPath} onChange={(e) => set('ctaPath', e.target.value as CtaPath | '')} style={inputStyle}>
            <option value="">None</option>
            {CTA_OPTIONS.map((o) => (
              <option key={o.path} value={o.path}>
                {o.label} ({o.path})
              </option>
            ))}
          </select>
        </div>
        {form.ctaPath && (
          <div>
            <FieldLabel htmlFor="blog-cta-label" aside={<Counter n={form.ctaLabel.length} max={BLOG_LIMITS.ctaLabelMax} />}>
              Button text
            </FieldLabel>
            <input id="blog-cta-label" value={form.ctaLabel} placeholder="Build my card" onChange={(e) => set('ctaLabel', e.target.value)} style={inputStyle} />
          </div>
        )}
        <FieldError message={errors.cta} />
      </Section>
    </aside>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section
      style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <h3 className="mono" style={{ margin: 0, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 600 }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function SearchPreview({ title, slug, excerpt }: { title: string; slug: string; excerpt: string }) {
  const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 10, background: '#fff', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ fontSize: 12, color: '#4d5156', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {siteHost()} › blog › {slug || '…'}
      </div>
      <div style={{ fontSize: 17, color: '#1a0dab', lineHeight: 1.3, margin: '2px 0' }}>{clip(title, 60)}</div>
      <div style={{ fontSize: 13, color: '#4d5156', lineHeight: 1.45 }}>{excerpt ? clip(excerpt, 160) : 'Add an excerpt.'}</div>
    </div>
  );
}

// ── Tags ──────────────────────────────────────────────────────────────

function TagInput({
  id,
  value,
  onChange,
  suggestions,
  invalid,
}: {
  id: string;
  value: string[];
  onChange: (v: string[]) => void;
  suggestions: string[];
  invalid: boolean;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const chosen = new Set(value.map((v) => slugify(v)));
  const q = draft.trim().toLowerCase();
  const matches = suggestions
    .filter((s) => !chosen.has(slugify(s)) && (!q || s.toLowerCase().includes(q)))
    .slice(0, 8);
  const full = value.length >= BLOG_LIMITS.tagsMax;

  const add = (name: string) => {
    const clean = name.trim().replace(/\s+/g, ' ').slice(0, BLOG_LIMITS.tagNameMax);
    if (!clean || full || chosen.has(slugify(clean))) {
      setDraft('');
      return;
    }
    onChange([...value, clean]);
    setDraft('');
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (open && matches[active] && q) add(matches[active]);
      else add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        style={{
          ...inputStyle,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 4,
          padding: 4,
          borderColor: invalid ? '#a8331f' : 'var(--line-strong)',
        }}
        onClick={() => document.getElementById(id)?.focus()}
      >
        {value.map((t) => (
          <span
            key={t}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--chip)', borderRadius: 100, padding: '2px 4px 2px 10px', fontSize: 12 }}
          >
            {t}
            <button
              type="button"
              aria-label={`Remove ${t}`}
              onClick={() => onChange(value.filter((v) => v !== t))}
              style={{ width: 18, height: 18, borderRadius: 100, color: 'var(--ink-3)', fontSize: 13, lineHeight: 1 }}
            >
              ×
            </button>
          </span>
        ))}
        {!full && (
          <input
            id={id}
            value={draft}
            role="combobox"
            aria-expanded={open && matches.length > 0}
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            onChange={(e) => {
              setDraft(e.target.value);
              setOpen(true);
              setActive(0);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              setTimeout(() => setOpen(false), 120);
              if (draft.trim()) add(draft);
            }}
            onKeyDown={onKeyDown}
            placeholder={value.length ? '' : 'Add a tag'}
            style={{ flex: 1, minWidth: 80, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, padding: '3px 4px' }}
          />
        )}
      </div>
      {open && matches.length > 0 && (
        <ul
          id={`${id}-list`}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 10,
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            padding: 4,
            listStyle: 'none',
            background: 'var(--surface)',
            border: '1px solid var(--line-strong)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {matches.map((m, i) => (
            <li
              key={m}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                add(m);
              }}
              onMouseEnter={() => setActive(i)}
              style={{ padding: '5px 8px', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: i === active ? 'var(--surface-2)' : 'transparent' }}
            >
              {m}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Markdown toolbar ──────────────────────────────────────────────────

type FormatAction = 'bold' | 'italic' | 'h2' | 'h3' | 'link' | 'quote' | 'list' | 'code' | 'image';

const TOOLBAR: { action: FormatAction; label: ReactNode; title: string; style?: CSSProperties }[] = [
  { action: 'bold', label: 'B', title: 'Bold (Ctrl+B)', style: { fontWeight: 800 } },
  { action: 'italic', label: 'I', title: 'Italic (Ctrl+I)', style: { fontStyle: 'italic', fontFamily: 'var(--font-display)' } },
  { action: 'h2', label: 'H2', title: 'Heading' },
  { action: 'h3', label: 'H3', title: 'Subheading' },
  { action: 'link', label: 'Link', title: 'Link (Ctrl+K)' },
  { action: 'quote', label: '“ ”', title: 'Quote' },
  { action: 'list', label: '• List', title: 'Bulleted list' },
  { action: 'code', label: '</>', title: 'Code', style: { fontFamily: 'var(--font-mono)' } },
  { action: 'image', label: 'Image', title: 'Upload an image' },
];

function Toolbar({ onAction }: { onAction: (a: FormatAction) => void }) {
  return (
    <div
      role="toolbar"
      aria-label="Formatting"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        padding: 4,
        border: '1px solid var(--line-strong)',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        background: 'var(--surface-2)',
      }}
    >
      {TOOLBAR.map((t) => (
        <button
          key={t.action}
          type="button"
          title={t.title}
          aria-label={t.title}
          // Keep the textarea's selection: don't let the button take focus.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onAction(t.action)}
          style={{ minWidth: 30, height: 28, padding: '0 8px', borderRadius: 6, fontSize: 13, color: 'var(--ink-2)', ...t.style }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Replace [start, end) in the textarea with `text`, going through
 * execCommand where available so the browser's undo stack keeps working.
 */
function replaceRange(
  ta: HTMLTextAreaElement,
  start: number,
  end: number,
  text: string,
  fallback: (value: string) => void,
  select?: [number, number],
) {
  ta.focus();
  ta.setSelectionRange(start, end);
  let ok = false;
  try {
    ok = document.execCommand('insertText', false, text);
  } catch {
    ok = false;
  }
  if (!ok) fallback(ta.value.slice(0, start) + text + ta.value.slice(end));
  const [s, e] = select ?? [start + text.length, start + text.length];
  requestAnimationFrame(() => ta.setSelectionRange(s, e));
}

function applyFormat(ta: HTMLTextAreaElement, action: FormatAction, fallback: (v: string) => void) {
  const { selectionStart: s, selectionEnd: e, value } = ta;
  const sel = value.slice(s, e);

  const wrap = (before: string, after: string, placeholder: string) => {
    const inner = sel || placeholder;
    replaceRange(ta, s, e, before + inner + after, fallback, [s + before.length, s + before.length + inner.length]);
  };

  // Line-based actions work on every line the selection touches.
  const lineStart = value.lastIndexOf('\n', s - 1) + 1;
  const nl = value.indexOf('\n', e);
  const lineEnd = nl === -1 ? value.length : nl;
  const lines = value.slice(lineStart, lineEnd).split('\n');
  const setLines = (next: string[]) => {
    const text = next.join('\n');
    replaceRange(ta, lineStart, lineEnd, text, fallback, [lineStart, lineStart + text.length]);
  };
  const togglePrefix = (prefix: string) => {
    const all = lines.every((l) => l.startsWith(prefix));
    setLines(lines.map((l) => (all ? l.slice(prefix.length) : prefix + l)));
  };
  const heading = (hashes: string) => {
    const prefix = `${hashes} `;
    const all = lines.every((l) => l.startsWith(prefix));
    setLines(lines.map((l) => (all ? l.slice(prefix.length) : prefix + l.replace(/^#{1,6}\s+/, ''))));
  };

  switch (action) {
    case 'bold':
      return wrap('**', '**', 'bold text');
    case 'italic':
      return wrap('_', '_', 'italic text');
    case 'h2':
      return heading('##');
    case 'h3':
      return heading('###');
    case 'quote':
      return togglePrefix('> ');
    case 'list':
      return togglePrefix('- ');
    case 'code':
      return sel.includes('\n') ? wrap('```\n', '\n```', '') : wrap('`', '`', 'code');
    case 'link': {
      const label = sel || 'link text';
      const text = `[${label}](https://)`;
      const urlStart = s + label.length + 3;
      replaceRange(ta, s, e, text, fallback, [urlStart, urlStart + 'https://'.length]);
      return;
    }
    case 'image':
      return;
  }
}

function onShortcut(e: KeyboardEvent<HTMLTextAreaElement>, fallback: (v: string) => void) {
  if (!(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
  const key = e.key.toLowerCase();
  const action: FormatAction | null = key === 'b' ? 'bold' : key === 'i' ? 'italic' : key === 'k' ? 'link' : null;
  if (!action) return;
  e.preventDefault();
  applyFormat(e.currentTarget, action, fallback);
}

function pickFiles(onFiles: (files: File[]) => void, multiple = true) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = IMAGE_TYPES.join(',');
  input.multiple = multiple;
  input.onchange = () => onFiles(input.files ? Array.from(input.files) : []);
  input.click();
}

// ── Unsaved changes ───────────────────────────────────────────────────

const LEAVE_PROMPT = 'You have unsaved changes. Leave without saving?';
const REFRESH_PROMPT = 'You have unsaved changes. Discard them and load the latest saved version?';

/**
 * Warn before leaving with unsaved edits: the browser's own prompt for
 * reloads and closing the tab, and a confirm for in-app link clicks (the
 * console uses <BrowserRouter>, which has no navigation blocker).
 */
function useUnsavedChangesGuard(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank' || a.origin !== window.location.origin) return;
      if (a.pathname === window.location.pathname && a.search === window.location.search) return;
      if (!window.confirm(LEAVE_PROMPT)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [active]);
}

const linkButton: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--coral-ink)',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
};
